/**
 * `navigator.share` payload with the link in both `text` and `url`.
 *
 * Decision (2026-10): prefer Copy + Facebook/LinkedIn reliability over a clean WhatsApp body.
 *
 * | Target                         | Link only in `url`     | Link only in `text`       | Link in `text` + `url` (chosen) |
 * |--------------------------------|------------------------|---------------------------|----------------------------------|
 * | iOS/Android share-sheet Copy   | drops link             | includes link             | includes link                    |
 * | WhatsApp / similar (join both) | link once              | link once (linkified)     | link often twice                 |
 * | Facebook / LinkedIn            | ok (need `url`)        | may drop link             | ok                               |
 * | iMessage / Mail / Messages     | usually ok             | usually ok                | usually ok                       |
 *
 * We cannot hide system share-sheet Copy. In-app Copy still uses `copyShareText` (message + link).
 */
export function nativeShareData(message: string, url: string, title?: string): ShareData {
    const link = url.trim();
    const text = [message.trim(), link].filter(Boolean).join("\n");
    return { ...(title ? { title } : {}), ...(text ? { text } : {}), ...(link ? { url: link } : {}) };
}

/** User dismissed the share sheet — not a failure, so don't fall back to copying. */
export function isShareCancelled(error: unknown): boolean {
    return (error as DOMException | null)?.name === "AbortError";
}

export function canNativeShare(): boolean {
    return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** Copies message + link; resolves false when the clipboard is unavailable or denied. */
export function copyShareText(message: string, url: string): Promise<boolean> {
    const text = [message.trim(), url.trim()].filter(Boolean).join("\n");
    if (!text || typeof navigator === "undefined" || !navigator.clipboard) return Promise.resolve(false);
    return navigator.clipboard.writeText(text).then(
        () => true,
        () => false
    );
}
